import stemmer from "snowball-german";
import { deu as germanStopwords } from "stopword";

interface Document {
    id: string;
    text: string;
}

interface PostingList {
    [docId: string]: number; // docId -> term frequency
}

interface InvertedIndexEntry {
    [term: string]: PostingList;
}

export class InvertedIndex {
    private index: InvertedIndexEntry = {};
    private documentCount: number = 0;
    private documents: Set<string> = new Set();
    private stopwords: Set<string>;

    constructor() {
        // Convert array of stopwords to Set for O(1) lookup
        this.stopwords = new Set(germanStopwords);
    }

    // Text preprocessing pipeline
    private preprocess(text: string): string[] {
        // Remove punctuation and special characters
        const cleanText = text
            .toLowerCase()
            .replace(/[.,!?:;()"'\-\[\]{}]/g, " ")
            .replace(/\s+/g, " ")
            .trim();

        // Tokenize by whitespace
        const tokens = cleanText.split(" ");

        // Filter empty tokens, stopwords, and apply German stemming
        return tokens
            .filter((token) => token.length > 0)
            .filter((token) => !this.stopwords.has(token))
            .map((token) => stemmer(token));
    }

    // Calculate term frequency for a single document
    private calculateTF(tokens: string[]): Map<string, number> {
        const tf = new Map<string, number>();

        for (const token of tokens) {
            tf.set(token, (tf.get(token) || 0) + 1);
        }

        // Normalize term frequencies by document length
        const docLength = tokens.length;
        for (const [term, freq] of tf.entries()) {
            tf.set(term, freq / docLength);
        }

        return tf;
    }

    // Calculate IDF for a term
    private calculateIDF(term: string): number {
        const docsWithTerm = Object.keys(this.index[term] || {}).length;
        if (docsWithTerm == 0) return 0;
        return Math.log(this.documentCount / docsWithTerm);
    }

    // Add a single document to the index
    public addDocument(document: Document): void {
        const tokens = this.preprocess(document.text);
        const termFrequencies = this.calculateTF(tokens);

        // Update document count if this is a new document
        if (!this.documents.has(document.id)) {
            this.documents.add(document.id);
            this.documentCount++;
        }

        // Add terms to inverted index
        for (const [term, tf] of termFrequencies.entries()) {
            if (!this.index[term]) {
                this.index[term] = {};
            }
            this.index[term][document.id] = tf;
        }
    }

    // Vectorize a text (used for both documents and queries)
    public vectorize(text: string): Map<string, number> {
        const tokens = this.preprocess(text);
        const tf = this.calculateTF(tokens);
        const tfidf = new Map<string, number>();

        for (const [term, termFreq] of tf.entries()) {
            const idf = this.calculateIDF(term);
            tfidf.set(term, termFreq * idf);
        }

        return tfidf;
    }

    // Search for documents similar to a query
    public search(query: string, topK: number = 10): Array<{ docId: string; score: number }> {
        const queryVector = this.vectorize(query);
        const scores = new Map<string, number>();

        // Calculate cosine similarity between query and each document
        for (const [queryTerm, queryWeight] of queryVector.entries()) {
            const postings = this.index[queryTerm] || {};

            for (const [docId, tf] of Object.entries(postings)) {
                const idf = this.calculateIDF(queryTerm);
                const docWeight = tf * idf;
                scores.set(docId, (scores.get(docId) || 0) + queryWeight * docWeight);
            }
        }

        // Sort documents by score
        return Array.from(scores.entries())
            .map(([docId, score]) => ({ docId, score }))
            .sort((a, b) => b.score - a.score)
            .slice(0, topK);
    }

    // Get index statistics
    public getStats() {
        return {
            documentCount: this.documentCount,
            vocabularySize: Object.keys(this.index).length,
        };
    }
}
